import React, { Component } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonMenu,
  IonItem,
  IonList,
  IonMenuButton,
  IonButtons,
  IonBackButton,
  IonReorderGroup,
  IonReorder,
  IonLabel,
  IonButton,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonModal,
} from "@ionic/react";
import { isPlatform, getPlatforms, ItemReorderEventDetail } from "@ionic/core";
import { RouteComponentProps, withRouter } from "react-router";

import { AppLauncher } from "@capacitor/app-launcher";
import { Browser } from "@capacitor/browser";

// CSS
import "./EditDashboardPage.css";
import "../../Shared.css";
import "../../../App.css";

// Images
import iconAdd from "../../assets/Button_Edit_Add@3x.png";
import iconRemove from "../../assets/Button_Edit_Remove@3x.png";

// Controllers
import {
  IScreenProps,
  IUser,
  IGuideline,
  IAlgorithm,
  IPatientAge,
  ISignificantChange,
  IFurtherResource,
  IGuidelineSummary,
  IClinicalNotice,
  IDashboardCardState,
} from "../../Interfaces";
import AppController from "../../../controllers/AppController";
import DataController from "../../controllers/DataController";
import RemoteDataController from "../../controllers/RemoteDataController";

// Components
import AppHeader from "../../components/AppHeader";
import AppCard from "../../components/AppCard";
import GuidelineCell from "../../components/GuidelineCell";
import SearchResultCell from "../../components/SearchResultCell";
import LinkCell from "../../components/LinkCell";
import AgeSelector from "../../components/AgeSelector";
import SignificantChangeCell from "../../components/SignificantChangeCell";
import FurtherResourceCell from "../../components/FurtherResourceCell";
import MessageBar from "../../components/MessageBar";
import Dropdown from "../../components/Dropdown";

import _, { words } from "lodash";
import ClinicalNoticeCell from "../../components/ClinicalNoticeCell";

type Props = RouteComponentProps<{ id: string; tab: string }> & { screenProps: IScreenProps; features: string[] };
type State = {
  dashboardCardStates: IDashboardCardState[];
};

class EditDashboardPage extends Component<Props, State> {
  private searchTextField: React.RefObject<HTMLIonInputElement>;

  constructor(props: Props) {
    super(props);

    this.searchTextField = React.createRef();

    this.state = {
      dashboardCardStates: [],
    };
  }

  async componentDidMount() {
    let dashboardCardStates = await DataController.getDashboardCardStates(
      this.props.screenProps.trust,
      this.props.screenProps.testMode,
      this.props.screenProps.app
    );
    this.setState({ dashboardCardStates: dashboardCardStates });
  }

  async reorderDashboardCards(event: CustomEvent<ItemReorderEventDetail>) {
    // The `from` and `to` properties contain the index of the item
    // when the drag started and ended, respectively
    console.log("Dragged from index", event.detail.from, "to", event.detail.to);

    // Finish the reorder and position the item in the DOM based on
    // where the gesture ended. This method can also be called directly
    // by the reorder group

    let dashboardCardStates = this.state.dashboardCardStates;

    // reposition card
    if (event.detail.from < dashboardCardStates.length) {
      var element = dashboardCardStates[event.detail.from];
      dashboardCardStates.splice(event.detail.from, 1);
      dashboardCardStates.splice(event.detail.to, 0, element);

      let positionChange = element.name + " - ";
      if (event.detail.from >= event.detail.to) {
        positionChange += "up " + (event.detail.from - event.detail.to);
      } else {
        positionChange += "down " + (event.detail.to - event.detail.from);
      }

      RemoteDataController.logEvent("dashboard_reordered", {
        cardName: element.name,
        positionChange: positionChange,
        abTestingVersion: this.props.screenProps.abTestingVersion,
      });
    }

    for (
      let dashboardCardStateIndex = 0;
      dashboardCardStateIndex < dashboardCardStates.length;
      dashboardCardStateIndex++
    ) {
      let dashboardCardState = dashboardCardStates[dashboardCardStateIndex];

      dashboardCardState.position = dashboardCardStateIndex;

      if (dashboardCardState.name === "Updates & Notices") {
        dashboardCardState.position = 0;
      }
      if (dashboardCardState.name === "Clinical Notices") {
        dashboardCardState.position = 0;
      }
      if (dashboardCardState.name === "Local Clinical Updates") {
        dashboardCardState.position = 0;
      }

      if (dashboardCardState.name === "Feedback") {
        dashboardCardState.position = 1000;
      }

      if (dashboardCardState.name === "Disclaimer") {
        dashboardCardState.position = 1001;
      }
    }

    await DataController.saveDashboardCardStates(dashboardCardStates);

    event.detail.complete();

    dashboardCardStates = await DataController.getDashboardCardStates(
      this.props.screenProps.trust,
      this.props.screenProps.testMode,
      this.props.screenProps.app
    );
    this.setState({ dashboardCardStates: dashboardCardStates });
  }

  async addDashboardCard(dashboardCardToAdd: IDashboardCardState) {
    let dashboardCardStates = this.state.dashboardCardStates;

    for (
      let dashboardCardStateIndex = 0;
      dashboardCardStateIndex < dashboardCardStates.length;
      dashboardCardStateIndex++
    ) {
      let dashboardCardState = dashboardCardStates[dashboardCardStateIndex];

      if (dashboardCardState.name === dashboardCardToAdd.name) {
        dashboardCardState.removed = false;
        dashboardCardState.position = dashboardCardStates.length;
      }
    }

    await DataController.saveDashboardCardStates(dashboardCardStates);
    dashboardCardStates = await DataController.getDashboardCardStates(
      this.props.screenProps.trust,
      this.props.screenProps.testMode,
      this.props.screenProps.app
    );
    this.setState({ dashboardCardStates: dashboardCardStates });
  }

  async removeDashboardCard(dashboardCardToRemove: IDashboardCardState) {
    let dashboardCardStates = this.state.dashboardCardStates;

    for (
      let dashboardCardStateIndex = 0;
      dashboardCardStateIndex < dashboardCardStates.length;
      dashboardCardStateIndex++
    ) {
      let dashboardCardState = dashboardCardStates[dashboardCardStateIndex];

      if (dashboardCardState.name === dashboardCardToRemove.name) {
        dashboardCardState.removed = true;
      }
    }

    await DataController.saveDashboardCardStates(dashboardCardStates);
    dashboardCardStates = await DataController.getDashboardCardStates(
      this.props.screenProps.trust,
      this.props.screenProps.testMode,
      this.props.screenProps.app
    );
    this.setState({ dashboardCardStates: dashboardCardStates });
  }

  render() {
    // render dashboard cards
    let dashboardCardsLayout = [];
    let unmoveableTopDashboardCardsLayout = [];
    let unmoveableBottomDashboardCardsLayout = [];
    let removedDashboardCardsLayout = [];

    for (let dashboardCardState of this.state.dashboardCardStates) {
      let shouldShow = true;
      let isRemoved = false;

      // hide cards which don't match enabled features
      if (dashboardCardState.name === "Patient Group Directions" && this.props.features.includes("pgds") === false) {
        shouldShow = false;
      }
      if (
        dashboardCardState.name === "Trust Guidelines" &&
        this.props.features.includes("trustGuidelinesDashboard") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Prescription Medicine Authorisations" &&
        this.props.features.includes("pmas") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Supplementary Medication Guidelines" &&
        this.props.features.includes("smgs") === false
      ) {
        shouldShow = false;
      } else if (dashboardCardState.name === "COVID-19" && this.props.features.includes("covidDashboard") === false) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Time Critical" &&
        this.props.features.includes("timeCritical") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Shared Procedures" &&
        this.props.features.includes("sharedProcedures") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Patient Age" &&
        this.props.features.includes("patientAgeDashboard") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Page for Age" &&
        this.props.features.includes("pageForAgeDashboard") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Further Resources" &&
        (this.props.features.includes("furtherResources") === false ||
          this.props.screenProps.furtherResources.length === 0)
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Guideline Updates" &&
        this.props.features.includes("significantChanges") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Trust Pins" &&
        (this.props.features.includes("trustPins") === false || this.props.screenProps.trustPins.length === 0)
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Staff Group" &&
        this.props.features.includes("staffGroupDashboard") === false
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Updates & Notices" ||
        dashboardCardState.name === "Clinical Notices" ||
        dashboardCardState.name === "Local Clinical Updates" ||
        dashboardCardState.name === "Feedback" ||
        dashboardCardState.name === "Disclaimer"
      ) {
        shouldShow = false;
      } else if (
        dashboardCardState.name === "Your subscriptions" &&
        this.props.features.includes("dashboardYourSubscriptions") === false
      ) {
        shouldShow = false;
      }

      if (dashboardCardState.removed) {
        isRemoved = true;
      }

      let cardName = dashboardCardState.name;

      if (dashboardCardState.name === "Trust Pins" && this.props.screenProps.trust !== "") {
        cardName = `${DataController.getTrustDisplayName(this.props.screenProps.trust)} Pins`;
      }

      if (
        dashboardCardState.name === "Supplementary Medication Guidelines" &&
        this.props.screenProps.trust === "EMAS"
      ) {
        cardName = "Medication Supply Guidelines";
      }

      if (dashboardCardState.name === "Updates & Notices") {
        if (this.props.screenProps.trust !== "") {
          unmoveableTopDashboardCardsLayout.push(
            <div key={dashboardCardState.name} className={`editDashboardCardWrapper`}>
              <div className="editDashboardCard unmoveableDashboardCard">{cardName}</div>
            </div>
          );
        }
      }

      if (dashboardCardState.name === "Clinical Notices") {
        if (this.props.features.includes("clinicalNotices")) {
          if (this.props.screenProps.trust !== "") {
            unmoveableTopDashboardCardsLayout.push(
              <div key={dashboardCardState.name} className={`editDashboardCardWrapper`}>
                <div className="editDashboardCard unmoveableDashboardCard">{cardName}</div>
              </div>
            );
          }
        }
      }

      if (dashboardCardState.name === "Local Clinical Updates") {
        if (this.props.features.includes("localClinicalUpdates")) {
          if (this.props.screenProps.trust !== "") {
            unmoveableTopDashboardCardsLayout.push(
              <div key={dashboardCardState.name} className={`editDashboardCardWrapper`}>
                <div className="editDashboardCard unmoveableDashboardCard">{cardName}</div>
              </div>
            );
          }
        }
      }

      dashboardCardsLayout.push(
        <div
          key={dashboardCardState.name}
          className={`editDashboardCardWrapper ${!shouldShow || isRemoved ? "editDashboardCardHidden" : ""}`}
        >
          <div className="editDashboardCard">
            <div className="editDashboardCardStart">
              {dashboardCardState.removable ? (
                <img
                  alt=""
                  className="icon-32 editDashboardAddRemoveIcon"
                  onClick={this.removeDashboardCard.bind(this, dashboardCardState)}
                  src={iconRemove}
                />
              ) : (
                <div className="icon-32 editDashboardAddRemoveIcon" />
              )}
              {cardName}
            </div>
            <IonReorder slot="end" />
          </div>
        </div>
      );

      if (isRemoved) {
        removedDashboardCardsLayout.push(
          <div
            key={dashboardCardState.name}
            className={`editDashboardCardWrapper ${!shouldShow || !isRemoved ? "editDashboardCardHidden" : ""}`}
          >
            <div className="editDashboardCard">
              <div className="editDashboardCardStart">
                {dashboardCardState.removable ? (
                  <img
                    alt=""
                    className="icon-32 editDashboardAddRemoveIcon"
                    onClick={this.addDashboardCard.bind(this, dashboardCardState)}
                    src={iconAdd}
                  />
                ) : (
                  <div className="icon-32 editDashboardAddRemoveIcon" />
                )}
                {cardName}
              </div>
            </div>
          </div>
        );
      }

      if (
        dashboardCardState.name === "Feedback" ||
        (dashboardCardState.name === "Disclaimer" && this.props.screenProps.app === "CFR")
      ) {
        unmoveableBottomDashboardCardsLayout.push(
          <div key={dashboardCardState.name} className={`editDashboardCardWrapper`}>
            <div className="editDashboardCard unmoveableDashboardCard">{cardName}</div>
          </div>
        );
      }
    }

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar mode="ios" className="navBar">
            <IonButtons slot="start" className="headerButtonText" color="#FFFFFF">
              <IonBackButton defaultHref={`/${this.props.match.params.tab}`} class="headerButtonText" />
            </IonButtons>
            <IonTitle>Edit Dashboard</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <div className="editDashboardContentContainer globalContentMargin">
            <div className="editDashboardInfoText">
              Customise your dashboard by arranging the cards into your preferred order. Press and hold the grip icon on
              the right side of the card to move it. You can also hide cards from your dashboard by tapping the remove
              button on the left side of the card. Some cards cannot be moved or hidden from your dashboard.
            </div>

            <div className="editDashboardSectionLabel">CURRENT CARDS</div>

            {unmoveableTopDashboardCardsLayout}

            <IonReorderGroup disabled={false} onIonItemReorder={this.reorderDashboardCards.bind(this)}>
              {dashboardCardsLayout}
            </IonReorderGroup>

            {unmoveableBottomDashboardCardsLayout}

            <div className="editDashboardSectionLabel">AVAILABLE CARDS</div>

            {removedDashboardCardsLayout}
          </div>
        </IonContent>
      </IonPage>
    );
  }
}

export default withRouter(EditDashboardPage);
